import type { RequestBody, RequestQuery, ResponseOf } from '@shared/contracts';
import { api } from '@/store/api';

/**
 * Notification endpoints.
 *
 * The mutations all return the authoritative unread count and write it into the
 * `UnreadCount` cache entry directly, rather than invalidating and refetching. Two
 * reasons: the number is already in the response, so a refetch is a wasted round trip;
 * and a refetch leaves a window in which the badge shows the old value, which is
 * exactly the flicker this design exists to avoid.
 */

type UnreadResult = ResponseOf<'notifications.unreadCount'>;

export const notificationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listNotifications: build.query<
      ResponseOf<'notifications.list'>,
      RequestQuery<'notifications.list'>
    >({
      query: (query) => ({ id: 'notifications.list', options: { query } }),
      providesTags: ['Notification'],
    }),

    unreadCount: build.query<UnreadResult, void>({
      query: () => ({ id: 'notifications.unreadCount' }),
      providesTags: ['UnreadCount'],
    }),

    markNotificationRead: build.mutation<UnreadResult, string>({
      query: (id) => ({ id: 'notifications.markRead', options: { params: { id } } }),
      invalidatesTags: ['Notification'],
      onQueryStarted: adoptServerCount,
    }),

    dismissNotification: build.mutation<UnreadResult, string>({
      query: (id) => ({ id: 'notifications.dismiss', options: { params: { id } } }),
      invalidatesTags: ['Notification'],
      onQueryStarted: adoptServerCount,
    }),

    markAllRead: build.mutation<UnreadResult, void>({
      query: () => ({ id: 'notifications.markAllRead' }),
      invalidatesTags: ['Notification'],
      onQueryStarted: adoptServerCount,
    }),

    getNotificationPreferences: build.query<
      ResponseOf<'notifications.preferences.get'>,
      void
    >({
      query: () => ({ id: 'notifications.preferences.get' }),
      providesTags: ['NotificationPreferences'],
    }),

    updateNotificationPreferences: build.mutation<
      ResponseOf<'notifications.preferences.update'>,
      RequestBody<'notifications.preferences.update'>
    >({
      query: (body) => ({ id: 'notifications.preferences.update', options: { body } }),
      invalidatesTags: ['NotificationPreferences'],
    }),

    streamTicket: build.mutation<ResponseOf<'notifications.streamTicket'>, void>({
      query: () => ({ id: 'notifications.streamTicket' }),
    }),
  }),
});

/**
 * Write the count the server returned straight into the cache.
 *
 * Never `count - 1`: two tabs, a background job and a colleague's action all change it,
 * and a client that does its own arithmetic drifts within minutes.
 */
async function adoptServerCount(
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => unknown;
    queryFulfilled: Promise<{ data: UnreadResult }>;
  },
): Promise<void> {
  try {
    const { data } = await queryFulfilled;
    dispatch(
      notificationsApi.util.updateQueryData('unreadCount', undefined, (draft) => {
        draft.data.unreadCount = data.data.unreadCount;
      }),
    );
  } catch {
    // The mutation failed; the cached count is still the last one the server gave us.
  }
}

export const {
  useListNotificationsQuery,
  useUnreadCountQuery,
  useMarkNotificationReadMutation,
  useDismissNotificationMutation,
  useMarkAllReadMutation,
  useGetNotificationPreferencesQuery,
  useUpdateNotificationPreferencesMutation,
  useStreamTicketMutation,
} = notificationsApi;
